import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SaveIcon from "@mui/icons-material/Save";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import ExplorerFilterBuilder from "./ExplorerFilterBuilder";
import AttendanceQuickFilters from "./AttendanceQuickFilters";
import ColumnManagerButton from "./ColumnManagerButton";
import { getOperator } from "./explorerFilterUtils";
import { DEFAULT_ATTENDANCE_STATE } from "./attendanceFilterConfig";

const ATTENDANCE_CHIP_LABELS = {
  employeeId: "Emp ID",
  employeeName: "Name",
  device: "Device",
  branch: "Branch",
  punchType: "Punch",
  status: "Status",
  fromDate: "From",
  toDate: "To",
  special: "Preset",
};

export default function ExplorerToolbar({
  collectionName,
  allCollections,
  onSelectCollection,
  isAttendance,
  globalSearch,
  onGlobalSearchChange,
  filters,
  fieldNames,
  fieldTypes,
  distinctFor,
  attendanceDistinctFor,
  onFiltersChange,
  attendance,
  onAttendanceChange,
  presets,
  onSavePreset,
  onLoadPreset,
  onDeletePreset,
  onReset,
  onRefresh,
  loading,
  counts,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  hasMore,
  onLoadMore,
  columnFields,
  columnPrefs,
  onColumnPrefsChange,
  onAutoWidth,
  onResetColumns,
  onExport,
}) {
  const [rawSearch, setRawSearch] = useState(globalSearch || "");
  const [exportAnchor, setExportAnchor] = useState(null);
  const [presetAnchor, setPresetAnchor] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [presetName, setPresetName] = useState("");

  useEffect(() => {
    setRawSearch(globalSearch || "");
  }, [globalSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (rawSearch !== globalSearch) onGlobalSearchChange(rawSearch);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawSearch]);

  const attendanceState = { ...DEFAULT_ATTENDANCE_STATE, ...(attendance || {}) };

  const chips = useMemo(() => {
    const list = [];
    if (globalSearch) {
      list.push({ key: "search", label: `Search: "${globalSearch}"`, onDelete: () => onGlobalSearchChange("") });
    }
    (filters || []).forEach((f) => {
      if (!f.field) return;
      const op = getOperator(f.op);
      const val = op.needsValue ? ` ${f.value}` : "";
      list.push({
        key: f.id,
        label: `${f.field} ${f.op}${val}`,
        onDelete: () => onFiltersChange(filters.filter((x) => x.id !== f.id)),
      });
    });
    if (isAttendance) {
      Object.keys(ATTENDANCE_CHIP_LABELS).forEach((dim) => {
        const value = attendanceState[dim];
        if (!value) return;
        list.push({
          key: `att_${dim}`,
          label: `${ATTENDANCE_CHIP_LABELS[dim]}: ${value}`,
          onDelete: () => onAttendanceChange({ ...attendanceState, [dim]: dim === "special" ? null : "" }),
        });
      });
    }
    return list;
  }, [globalSearch, filters, isAttendance, attendanceState, onGlobalSearchChange, onFiltersChange, onAttendanceChange]);

  const handleSavePreset = () => {
    const name = presetName.trim();
    if (!name) return;
    onSavePreset(name);
    setPresetName("");
    setSaveOpen(false);
  };

  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 1.5, position: "sticky", top: 0, zIndex: 5 }}>
      {/* Row 1: collection + search + actions */}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
        <TextField
          select
          size="small"
          label="Collection"
          value={allCollections.includes(collectionName) ? collectionName : ""}
          onChange={(e) => onSelectCollection(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          {allCollections.map((name) => (
            <MenuItem key={name} value={name}>{name}</MenuItem>
          ))}
        </TextField>

        <TextField
          size="small"
          placeholder="Global search (all loaded fields)…"
          value={rawSearch}
          onChange={(e) => setRawSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 220 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: rawSearch ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setRawSearch("")}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
        />

        <TextField
          select
          size="small"
          label="Page size"
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          sx={{ minWidth: 120 }}
        >
          {pageSizeOptions.map((n) => (
            <MenuItem key={n} value={n}>{n} / page</MenuItem>
          ))}
        </TextField>

        <Tooltip title="Refresh from Firestore">
          <span>
            <Button
              size="small"
              variant="contained"
              onClick={onRefresh}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            >
              Refresh
            </Button>
          </span>
        </Tooltip>

        <Button size="small" color="inherit" variant="outlined" startIcon={<RestartAltIcon />} onClick={onReset}>
          Reset
        </Button>

        <Button size="small" color="inherit" variant="outlined" startIcon={<SaveIcon />} onClick={() => setSaveOpen(true)}>
          Save Filter
        </Button>

        <Button
          size="small"
          color="inherit"
          variant="outlined"
          startIcon={<BookmarkIcon />}
          onClick={(e) => setPresetAnchor(e.currentTarget)}
        >
          Presets ({presets.length})
        </Button>

        <ColumnManagerButton
          fields={columnFields}
          prefs={columnPrefs}
          onChange={onColumnPrefsChange}
          onAutoWidth={onAutoWidth}
          onReset={onResetColumns}
        />

        <Button
          size="small"
          color="inherit"
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={(e) => setExportAnchor(e.currentTarget)}
        >
          Export
        </Button>
      </Stack>

      <Divider sx={{ my: 1.5 }} />

      {/* Row 2: attendance smart filters (only for Wins Attendance) */}
      {isAttendance && (
        <>
          <AttendanceQuickFilters
            state={attendanceState}
            onChange={onAttendanceChange}
            distinctFor={attendanceDistinctFor}
          />
          <Divider sx={{ my: 1.5 }} />
        </>
      )}

      {/* Row 3: generic field filter builder */}
      <ExplorerFilterBuilder
        filters={filters}
        fieldNames={fieldNames}
        fieldTypes={fieldTypes}
        distinctFor={distinctFor}
        onChange={onFiltersChange}
      />

      {/* Active chips + counts */}
      {chips.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
              Active:
            </Typography>
            {chips.map((chip) => (
              <Chip key={chip.key} size="small" label={chip.label} onDelete={chip.onDelete} />
            ))}
          </Stack>
        </>
      )}

      <Divider sx={{ my: 1.5 }} />
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip size="small" color="default" label={`Loaded: ${counts.loaded}`} />
        <Chip size="small" color="primary" variant="outlined" label={`Showing: ${counts.filtered}`} />
        <Chip size="small" variant="outlined" label={`Total in collection: ${counts.total}`} />
        {hasMore && (
          <Button size="small" onClick={onLoadMore} disabled={loading}>
            Load more (cursor)
          </Button>
        )}
        {loading && <CircularProgress size={16} />}
      </Stack>

      {/* Export menu */}
      <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}>
        {["excel", "csv", "json", "pdf", "print"].map((type) => (
          <MenuItem
            key={type}
            onClick={() => {
              setExportAnchor(null);
              onExport(type);
            }}
          >
            {type === "excel" && "Export Excel (.xlsx)"}
            {type === "csv" && "Export CSV"}
            {type === "json" && "Export JSON"}
            {type === "pdf" && "Export PDF"}
            {type === "print" && "Print"}
          </MenuItem>
        ))}
      </Menu>

      {/* Presets menu */}
      <Menu anchorEl={presetAnchor} open={Boolean(presetAnchor)} onClose={() => setPresetAnchor(null)}>
        {presets.length === 0 && (
          <MenuItem disabled>No saved filters for this collection</MenuItem>
        )}
        {presets.map((preset) => (
          <MenuItem
            key={preset.id}
            onClick={() => {
              setPresetAnchor(null);
              onLoadPreset(preset);
            }}
          >
            <Box sx={{ flex: 1 }}>{preset.name}</Box>
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                onDeletePreset(preset.id);
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </MenuItem>
        ))}
      </Menu>

      {/* Save preset dialog */}
      <Dialog open={saveOpen} onClose={() => setSaveOpen(false)}>
        <DialogTitle>Save current filters</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Preset name"
            value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSavePreset()}
            sx={{ mt: 1, minWidth: 320 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSavePreset}>Save</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
